import React, { useState } from 'react';
import { Outlet, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, 
  Search, 
  Film, 
  Send, 
  BarChart3, 
  Settings, 
  Menu,
  X,
  ChevronRight,
  Globe,
  CreditCard,
  Users,
  LogOut,
  User,
  Sparkles,
  Calendar,
  Bot,
  PieChart,
  Webhook,
  Shield,
  Scissors,
  Lock,
  Cpu
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useLanguage } from '@/lib/LanguageContext';
import { useAuth } from '@/lib/AuthContext';
import AIAssistant from './AIAssistant';
import { PWAInstallPrompt } from './PWAInstallPrompt';
import MobileBottomNav from './MobileBottomNav';
import Logo from './Logo';

export default function Layout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { language, setLanguage, t } = useLanguage();
  const { user, logout } = useAuth();

  const navItems = [
    { path: '/dashboard', label: t('nav.dashboard'), icon: LayoutDashboard },
    { path: '/discovery', label: t('nav.discovery'), icon: Search },
    { path: '/viral', label: 'Viral AI', icon: Sparkles },
    { path: '/clip-generator', label: 'Gerador de Clips', icon: Scissors },
    { path: '/autopilot', label: 'Autopilot', icon: Bot },
    { path: '/agent-api', label: 'API do Agente', icon: Cpu },
    { path: '/scheduler', label: 'Agendamento', icon: Calendar },
    { path: '/clips', label: t('nav.clipsLibrary'), icon: Film },
    { path: '/publishing', label: t('nav.publishing'), icon: Send },
    { path: '/analytics', label: t('nav.analytics'), icon: BarChart3 },
    { path: '/unified-analytics', label: 'Analytics Pro', icon: PieChart },
    { path: '/webhooks', label: 'Webhooks', icon: Webhook },
    { path: '/billing', label: 'Billing', icon: CreditCard },
    { path: '/affiliates', label: 'Affiliates', icon: Users },
    { path: '/vault', label: 'Cofre Seguro', icon: Lock },
    { path: '/security', label: 'Segurança', icon: Shield },
    { path: '/settings', label: t('nav.settings'), icon: Settings },
  ];

  const getPageTitle = () => {
    const item = navItems.find(item => item.path === location.pathname);
    return item ? item.label : t('nav.dashboard');
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const clipsUsed = user?.clips_used || 0;
  const clipsLimit = user?.clips_limit || 0;
  const usagePercent = clipsLimit > 0 ? (clipsUsed / clipsLimit) * 100 : 0;

  return (
    <div className="flex h-screen overflow-hidden bg-background">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside 
        className={`
          fixed lg:static inset-y-0 left-0 z-50
          w-[280px] bg-background/95 backdrop-blur-xl border-r border-border
          transform transition-transform duration-300 ease-in-out
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between h-16 px-6 border-b border-border">
            <div className="flex items-center gap-3">
              <img src="/logo.png" alt="Viral Clipes" className="w-10 h-10" />
              <div>
                <h1 className="font-heading font-bold text-lg tracking-tight">Viral Clipes</h1>
                <p className="text-xs text-muted-foreground capitalize">{user?.plan || 'Free'} Plan</p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden"
              onClick={() => setSidebarOpen(false)}
              data-testid="close-sidebar-btn"
            >
              <X className="w-5 h-5" />
            </Button>
          </div>

          {/* Usage */}
          {clipsLimit > 0 && (
            <div className="px-4 py-3 border-b border-border">
              <div className="flex justify-between text-xs mb-1">
                <span className="text-muted-foreground">Clips Used</span>
                <span>{clipsUsed} / {clipsLimit}</span>
              </div>
              <Progress value={usagePercent} className="h-1.5" />
            </div>
          )}

          {/* Navigation */}
          <ScrollArea className="flex-1 px-4 py-6">
            <nav className="space-y-2">
              {navItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                
                return (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    onClick={() => setSidebarOpen(false)}
                    data-testid={`nav-${item.path.slice(1)}`}
                    className={`
                      flex items-center gap-3 px-4 py-3 rounded-lg font-medium text-sm
                      transition-all duration-200
                      ${isActive 
                        ? 'bg-primary/15 text-primary border border-primary/30' 
                        : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                      }
                    `}
                  >
                    <Icon className="w-5 h-5" strokeWidth={1.5} />
                    <span>{item.label}</span>
                    {isActive && <ChevronRight className="w-4 h-4 ml-auto" />}
                  </NavLink>
                );
              })}
            </nav>
          </ScrollArea>

          {/* Footer */}
          <div className="p-4 border-t border-border">
            <div className="glass-card p-4">
              <p className="text-xs text-muted-foreground mb-2">{t('common.processingEngine')}</p>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                <span className="text-sm font-medium">{t('common.active')}</span>
              </div>
            </div>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <header className="h-16 border-b border-border bg-background/80 backdrop-blur-xl flex items-center justify-between px-6">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden"
              onClick={() => setSidebarOpen(true)}
              data-testid="open-sidebar-btn"
            >
              <Menu className="w-5 h-5" />
            </Button>
            <h2 className="font-heading font-semibold text-xl">{getPageTitle()}</h2>
          </div>
          
          <div className="flex items-center gap-4">
            {/* Language Selector */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="gap-2" data-testid="language-selector">
                  <Globe className="w-4 h-4" />
                  <span className="hidden sm:inline">{language === 'pt-BR' ? 'PT' : 'EN'}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => setLanguage('pt-BR')} data-testid="lang-pt-br">
                  🇧🇷 Português (BR)
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setLanguage('en')} data-testid="lang-en">
                  🇺🇸 English
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            
            {/* User Menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="gap-2" data-testid="user-menu">
                  <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
                    <User className="w-4 h-4 text-primary" />
                  </div>
                  <span className="hidden sm:inline text-sm">{user?.name}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-2 py-1.5">
                  <p className="text-sm font-medium">{user?.name}</p>
                  <p className="text-xs text-muted-foreground">{user?.email}</p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate('/billing')}>
                  <CreditCard className="w-4 h-4 mr-2" />
                  Billing
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/security')}>
                  <Shield className="w-4 h-4 mr-2" />
                  Segurança
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/settings')}>
                  <Settings className="w-4 h-4 mr-2" />
                  Settings
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-destructive focus:text-destructive" data-testid="logout-menu-item">
                  <LogOut className="w-4 h-4 mr-2" />
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Page Content - Add padding bottom for mobile nav */}
        <main className="flex-1 overflow-auto p-4 sm:p-6 pb-20 lg:pb-6 scrollbar-thin">
          <Outlet />
        </main>
      </div>

      {/* Mobile Bottom Navigation */}
      <MobileBottomNav />

      {/* AI Assistant */}
      <AIAssistant />
      
      {/* PWA Install Prompt */}
      <PWAInstallPrompt />
    </div>
  );
}
